import { useStore } from 'zustand';
import { useChartShapes, useChartUIContext } from '@/contexts';
import { useGlobalShapeSelector } from '@/contexts/GlobalShapesContext';
import { useChartUIStoreSelector } from '@/stores/chartUIStore';
import type { RenderNodesMap } from '@/types/commontypes';
import type { ChartId, ShapeType } from '@/types/commontypes';
import { useCallback, useEffect } from 'react';

export const useLockShape = () => {
  // Get from contexts
  const { shapeStore } = useChartShapes();
  const renderNodes = useStore(shapeStore, s => s.renderNodes);
  const setRenderNodes = useStore(shapeStore, s => s.setRenderNodes);
  const selectedShape = useGlobalShapeSelector(s => s.selectedShape);
  const { chartUIStore } = useChartUIContext();
  const setLocked = useChartUIStoreSelector(chartUIStore, s => s.setLocked);
  // Handle shape locking - currently same as hiding but can be extended to have different logic
  const toggleLock = useCallback(
    (chartId: ChartId, type: ShapeType, shapeUuid: string) => {
      const shapeList = renderNodes?.[chartId]?.[type];
      if (!shapeList) return;

      const updated: RenderNodesMap = { ...renderNodes };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-type-assertion -- RenderNodesMap의 mapped type에서 .map() 반환 타입 호환을 위한 캐스트
      (updated[chartId] as any)[type] = shapeList.map(shape =>
        shape.uuid === shapeUuid ? { ...shape, locked: !shape.locked } : shape,
      );

      setRenderNodes(updated);
      shapeStore.getState().markDirty(shapeUuid);

      // If the toggled shape is currently selected, sync UI state
      if (selectedShape?.uuid === shapeUuid && selectedShape?.chartId === chartId && selectedShape?.type === type) {
        const toggledShape = shapeList.find(s => s.uuid === shapeUuid);
        setLocked(!toggledShape?.locked);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shapeStore는 Context Provider 수명 동안 불변 참조이며 .getState()로 호출하므로 deps 불필요
    [renderNodes, selectedShape, setRenderNodes, setLocked],
  );

  const updateLockedState = useCallback(() => {
    if (!selectedShape) return;

    const { type, uuid, chartId } = selectedShape;
    if (!type || !chartId) return;

    const shapeList = renderNodes?.[chartId]?.[type];
    if (shapeList?.length) {
      const shape = shapeList.find(shape => shape.uuid === uuid);
      if (shape) setLocked(!!shape.locked);
    }
  }, [selectedShape, renderNodes, setLocked]);

  useEffect(() => {
    updateLockedState();
  }, [updateLockedState]);

  return { toggleLock };
};
